import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
    AlertTriangle,
    CheckCircle,
    ShieldAlert,
    Clock,
    Search,
    AlertCircle,
    MessageSquare,
    Info
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-800',
    warned: 'bg-orange-100 text-orange-800',
    banned: 'bg-red-100 text-red-800'
};

export default function AgencyReports() {
    const { getAuthHeader } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/agency/reports`, {
                headers: getAuthHeader()
            });
            setReports(response.data.reports || []);
        } catch (error) {
            console.error('Failed to fetch reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const filteredReports = reports.filter(r =>
        r.target_title?.toLowerCase().includes(search.toLowerCase()) ||
        r.reason.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="p-6 lg:p-8 space-y-6">
            <div>
                <h1 className="text-2xl font-heading font-bold text-navy-900">Reports Implementation</h1>
                <p className="text-navy-600">Review reports filed against your properties or account</p>
            </div>

            <div className="bg-white rounded-xl border border-navy-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-navy-100 bg-navy-50/50">
                    <div className="relative max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                            placeholder="Search by property or reason..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 bg-white"
                        />
                    </div>
                </div>

                {filteredReports.length === 0 ? (
                    <div className="p-12 text-center">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                        <h3 className="text-lg font-medium text-navy-900">No Reports Found</h3>
                        <p className="text-navy-500">Your account and properties have no active reports.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-navy-100">
                        {filteredReports.map((report) => (
                            <div key={report.id} className="p-4 hover:bg-navy-50 transition-colors">
                                <div className="flex flex-col md:flex-row gap-4 justify-between">
                                    <div className="space-y-2 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-navy-900">
                                                {report.target_title}
                                            </span>
                                            <Badge variant="outline" className="capitalize">
                                                {report.target_type}
                                            </Badge>
                                            <Badge variant="secondary" className={STATUS_COLORS[report.status] || 'bg-gray-100'}>
                                                {report.status.toUpperCase()}
                                            </Badge>
                                        </div>

                                        <div className="flex items-start gap-2 text-sm text-navy-700">
                                            <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                            <span>
                                                <span className="font-medium">Reason:</span> {report.reason}
                                            </span>
                                        </div>

                                        {report.description && (
                                            <div className="text-sm text-navy-600 bg-navy-50 p-3 rounded-lg border border-navy-100">
                                                "{report.description}"
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 text-xs text-navy-400">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(report.created_at)}
                                        </div>
                                    </div>

                                    {/* Admin Action Section */}
                                    {report.admin_notes && (
                                        <div className="md:w-1/3 bg-red-50 border border-red-100 rounded-lg p-3">
                                            <div className="flex items-center gap-2 mb-2 text-red-800 font-medium text-sm">
                                                <ShieldAlert className="w-4 h-4" />
                                                Admin Decision
                                            </div>
                                            <p className="text-sm text-red-700">
                                                {report.admin_notes}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
